import {
  repeatedMeasuresANOVA,
  type RepeatedMeasuresANOVAResult,
  type RepeatedMeasuresData,
} from '../statistics/repeatedMeasuresAnova';
import type {
  AnalysisOptions,
  ResultCache,
} from '../../stores/systems/keisuke/dashboardStore';
import type { DataRow } from '../../stores/systems/keisuke/dashboardStore';
import { parseFiniteNumberFromCell } from '../keisukeGridData';
import { kt, KeisukeAdapterError, formatAdapterPValue } from '../keisukeAdapterI18n';

function buildLongitudinalPaperText(result: RepeatedMeasuresANOVAResult): string {
  const methods =
    'A repeated-measures ANOVA was performed to assess the main effects of group and time and the group × time interaction on the outcome variable.';
  const group = result.groupEffect;
  const time = result.timeEffect;
  const interaction = result.interaction;
  const results =
    `Group effect: F(${group.dfBetween}, ${group.dfError}) = ${group.f.toFixed(2)}, ${formatAdapterPValue(group.pValue)}; ` +
    `Time effect: F(${time.dfBetween}, ${time.dfError}) = ${time.f.toFixed(2)}, ${formatAdapterPValue(time.pValue)}; ` +
    `Group × time interaction: F(${interaction.dfBetween}, ${interaction.dfError}) = ${interaction.f.toFixed(2)}, ${formatAdapterPValue(interaction.pValue)}.`;
  return `Methods\n\n${methods}\n\nResults\n\n${results}`;
}

export function runLongitudinalAdapter(data: DataRow[], options: AnalysisOptions): ResultCache {
  const mapping = options.mapping ?? {};
  const subjectCol = mapping.subjectColumn;
  const groupCol = mapping.groupColumn ?? mapping.factorAColumn;
  const timeCol = mapping.timeColumn;
  const valueCol = mapping.valueColumn;
  if (!subjectCol || !groupCol || !timeCol || !valueCol) {
    throw new KeisukeAdapterError('subjectGroupTimeValueRequired');
  }

  const repeatedMeasuresData: RepeatedMeasuresData[] = [];
  for (const row of data) {
    const subjectId = String(row[subjectCol] ?? '').trim();
    const group = String(row[groupCol] ?? '').trim();
    const time = parseFiniteNumberFromCell(row[timeCol]);
    const value = parseFiniteNumberFromCell(row[valueCol]);
    if (subjectId && group && time !== null && value !== null) {
      repeatedMeasuresData.push({ subjectId, group, time, value });
    }
  }
  if (repeatedMeasuresData.length === 0) {
    throw new KeisukeAdapterError('longitudinalDataNotFound');
  }

  const result = repeatedMeasuresANOVA(repeatedMeasuresData);
  const { f: fStatistic, pValue, dfBetween, dfError: dfWithin } = result.groupEffect;
  const stats: Record<string, unknown> = {
    fStatistic,
    pValue,
    dfBetween,
    dfWithin,
    timeEffectF: result.timeEffect.f,
    timeEffectP: result.timeEffect.pValue,
    interactionF: result.interaction.f,
    interactionP: result.interaction.pValue,
  };
  return {
    summary: kt('summary.longitudinal', {
      f: fStatistic.toFixed(3),
      p: pValue.toFixed(4),
    }),
    stats,
    statsDetailText: kt('statsDetail.longitudinal.line', {
      f: fStatistic,
      p: pValue,
    }),
    paperText: buildLongitudinalPaperText(result),
    updatedAt: Date.now(),
    rawResult: result,
    longitudinalChart: buildLongitudinalChartFromResult(result),
  };
}

function buildLongitudinalChartFromResult(
  result: RepeatedMeasuresANOVAResult
): NonNullable<ResultCache['longitudinalChart']> {
  return {
    series: result.groups.map((g) => ({
      group: g.name,
      points: g.means.map((m) => ({
        time: m.time,
        mean: m.mean,
        sd: m.stdDev,
      })),
    })),
  };
}
